import { connect } from 'react-redux';
import {
  getPodcastDetailStack,
  getAdjacentEpisodes
} from 'features/podcast/detail/actions';
import _ from 'lodash';
import React, { useEffect, Suspense } from 'react';
import styled from 'styled-components';
import { palette, lightTheme, darkTheme } from 'theme';
import MuiThemeProvider from '@material-ui/core/styles/MuiThemeProvider';
import CircularProgress from '@material-ui/core/CircularProgress';
import loadable from '@loadable/component';

import MetaTags from 'components/MetaTags';
import SwiftypeTags from 'components/SwiftypeTags';
import { SectionHeader, SectionWrapper } from 'components/Section';
import PrerenderSafeLazyLoad from 'components/PrerenderSafeLazyLoad';
import ContentTypography from 'components/ContentTypography';
import ResponsiveImg from 'components/ResponsiveImg';
import PaddedDivider from 'components/PaddedDivider';
import PodcastItem from '../elements/PodcastItem';
import PodcastPlayer from '../elements/PodcastPlayer';

import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Card from '@material-ui/core/Card';

import ChevronLeftIcon from 'mdi-material-ui/ChevronLeft';
import ChevronRightIcon from 'mdi-material-ui/ChevronRight';

import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import VideoPlayer from 'src/components/VideoPlayer';
dayjs.extend(duration);

const Container = loadable(() => import('components/Container'));
const StructuredData = loadable(() => import('components/StructuredData'));
const MixedRelatedContent = loadable(
  () => import('components/RelatedContent'),
  {
    resolveComponent: components => components.MixedRelatedContent
  }
);
const SocialShare = loadable(() => import('components/SocialShare'));
const SimpleFooterCTA = loadable(() => import('components/SimpleFooterCTA'));
const RelatedContentWrapper = loadable(
  () => import('components/RelatedContent'),
  {
    resolveComponent: components => components.RelatedContentWrapper
  }
);
const BlockContent = loadable(() => import('components/BlockContent'));
const LegacyPostContent = loadable(() =>
  import('views/Blog/Detail/elements/LegacyPostContent')
);

const PodcastImg = styled(ResponsiveImg)`
  height: 0;
  padding-bottom: 100%;
  overflow: visible;
  img {
    display: block;
    border-radius: 5px;
  }
`;

const PodcastItemPager = styled.div`
  color: white;
  display: flex;
  align-items: center;
  padding: 20px;
  svg {
    margin: 8px;
    width: 36px;
    height: 36px;
  }
`;

const MainContainer = styled(Container)`
  padding-bottom: 40px;
`;

const ProgressWrapper = styled.div`
  padding: 40px;
  text-align: center;
`;

const TranscriptCard = styled(Card)`
  padding: 40px;
`;

const PodcastDetail = React.memo(
  ({
    stack,
    adjacentEpisodes,
    loading,
    match,
    getPodcastDetailStack,
    getAdjacentEpisodes
  }) => {
    useEffect(() => {
      getPodcastDetailStack(match.url);
    }, [match.url, getPodcastDetailStack]);

    useEffect(() => {
      getAdjacentEpisodes(stack.episode_number);
    }, [stack.episode_number, getAdjacentEpisodes]);

    return loading ? (
      <ProgressWrapper>
        <CircularProgress />
      </ProgressWrapper>
    ) : (
      <React.Fragment>
        <MetaTags
          stack={stack}
          title={
            stack.meta?.title ||
            (stack.episode_number
              ? `CRISPR Cuts | Episode ${stack.episode_number}: ${stack.title}`
              : `CRISPR Cuts | ${stack.title}`)
          }
          description={stack.meta?.description || stack.description}
          image={stack.cover_image?.url || stack.meta?.image?.url}
        />
        <SwiftypeTags
          stack={stack}
          title={stack.title}
          excerpt={stack.description}
          publishedAt={stack.date}
          contentType="CRISPR Cuts Episode"
          image={stack.cover_image?.url}
        />
        <StructuredData
          partOfSeries="CRISPR Cuts"
          type="PodcastEpisode"
          headline={stack.title}
          image={[_.get(stack.cover_image, 'url')]}
          datePublished={stack.date}
          episodeNumber={stack.episode_number}
          duration={dayjs.duration(stack.duration, 'seconds').toISOString()}
        />

        <Suspense fallback={<CircularProgress />}>
          <MainContainer>
            <Grid container>
              <Grid item md={1} xs={12}>
                <SocialShare
                  url={window.location.href}
                  justify="center"
                  top="20px"
                />
              </Grid>
              <Grid item md={11} xs={12}>
                <Grid container spacing={40}>
                  {stack.cover_image && (
                    <Grid item md={4} sm={3} xs={12}>
                      <PodcastImg
                        src={stack.cover_image.url}
                        defaultWidth={400}
                        square
                      />
                    </Grid>
                  )}
                  <Grid item md={8} sm={9} xs={12}>
                    {stack.episode_number && (
                      <Typography variant="overline" component="p" gutterBottom>
                        Episode {stack.episode_number}
                      </Typography>
                    )}
                    <Typography gutterBottom variant="h2" component="h1">
                      {stack.title}
                    </Typography>
                    <ContentTypography
                      theme={darkTheme}
                      variant="body2"
                      dangerouslySetInnerHTML={{ __html: stack.description }}
                    />
                  </Grid>
                </Grid>
                <PaddedDivider spacing={1} />
                {stack.type === 'video' ? (
                  <VideoPlayer type="youtube" url={stack?.video?.youtube_url} />
                ) : (
                  <PodcastPlayer episode={stack} />
                )}
                <PaddedDivider spacing={1} hide />
                {(stack.blog_transcript?.[0] ||
                  stack.transcript?.sections?.length > 0) && (
                  <MuiThemeProvider theme={lightTheme}>
                    <TranscriptCard>
                      <Typography variant="overline" gutterBottom>
                        Transcript
                      </Typography>
                      <PaddedDivider spacing={1} />
                      {stack.blog_transcript?.[0] && (
                        <>
                          {stack.blog_transcript[0].enhanced_layout ? (
                            <BlockContent
                              sections={stack.blog_transcript[0].sections}
                            />
                          ) : (
                            <LegacyPostContent {...stack.blog_transcript[0]} />
                          )}
                        </>
                      )}
                      <BlockContent sections={stack.transcript.sections} />
                    </TranscriptCard>
                  </MuiThemeProvider>
                )}
                {stack.type !== 'video' && (
                  <Grid container spacing={40}>
                    {adjacentEpisodes.map((episode, idx) => (
                      <Grid item xs={12} sm={6} key={idx}>
                        <PodcastItemPager>
                          {episode.episode_number < stack.episode_number && (
                            <ChevronLeftIcon />
                          )}

                          <PodcastItem episode={episode} key={idx} small />
                          {episode.episode_number > stack.episode_number && (
                            <ChevronRightIcon />
                          )}
                        </PodcastItemPager>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Grid>
            </Grid>
          </MainContainer>

          {stack.related_content?.length > 0 && (
            <PrerenderSafeLazyLoad>
              <RelatedContentWrapper backgroundcolor={palette.grey[800]}>
                <Container>
                  <SectionHeader variant="overline" noGutter>
                    Explore Resources
                  </SectionHeader>
                  <Grid item xs={12}>
                    <MixedRelatedContent contentItems={stack.related_content} />
                  </Grid>
                </Container>
              </RelatedContentWrapper>
            </PrerenderSafeLazyLoad>
          )}
          {stack.footer_cta?.length > 0 && (
            <PrerenderSafeLazyLoad>
              <SectionWrapper>
                <SimpleFooterCTA {...stack.footer_cta[0]} />
              </SectionWrapper>
            </PrerenderSafeLazyLoad>
          )}
        </Suspense>
      </React.Fragment>
    );
  }
);

export default connect(
  state => ({
    stack: state.podcastDetail.stack,
    loading: state.podcastDetail.loading,
    adjacentEpisodes: state.podcastDetail.adjacentEpisodes
  }),
  { getPodcastDetailStack, getAdjacentEpisodes }
)(PodcastDetail);
